// per docs/ui-shape-role-first-dashboards.md, caiet UC26-01/02 + UC61-01/02/03
// The dashboard editor read from the role's side: pick an observer role, see
// exactly what that role can open today and how it reaches each one, author a
// new visualisation for it, or import one another role already has instead of
// re-authoring the same panel.
//
// It is a projection of analytics.dashboard_config.role_scope, the same column
// the dashboard-first list edits, over the same react-query cache key, so a
// change made here is visible there without a reload.
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudLink,
  MudModal,
  MudRadio,
  MudRadioGroup,
  MudSegmentedControl,
  MudSelect,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { EmptyState, onModalDismiss, onRowOpen, RowActionsMenu } from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { useObserverRoles, useRoleLabel } from '../../hooks/use-observer-roles';
import { useGrafanaDashboards } from '../../hooks/use-grafana-dashboards';
import { grafanaEditorSrc } from '../../features/dashboards/grafana-embed';
import {
  duplicateIdentity,
  grantKindFor,
  revokeBlockFor,
  stillReachedAfterRevoke,
  type GrantKind,
} from '../../features/dashboards/role-scope';
import './dashboards-admin.css';

interface DashboardView {
  id: string;
  slug: string;
  title: string;
  description: string;
  roleScope: string[];
  grafanaUid: string;
  refreshIntervalSeconds: number;
  defaultRange: string;
  maxRange: string;
  maxLookback: string;
  sortOrder: number;
  active: boolean;
  version: number;
  updatedAtIso: string;
}

// Sharing appends this role to the one definition's role_scope; copying writes a
// second, independently editable definition. The screen never guesses which one
// the administrator meant.
type ImportMode = 'share' | 'copy';

const TAG_SEMANTIC: Record<Exclude<GrantKind, 'none'>, 'brand' | 'accent'> = {
  specific: 'brand',
  'observer-family': 'accent',
};

function fetchDashboards(): Promise<DashboardView[]> {
  return apiClient
    .get<{ dashboards: DashboardView[] }>('/admin/dashboards')
    .then((r) => r.data.dashboards);
}

export function RoleDashboardsPanel(): JSX.Element {
  const { t } = useTranslate();
  const roleLabel = useRoleLabel();
  const definedRoles = useObserverRoles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // A Tolgee key, never the service's own sentence. The fallback is per
  // operation: a revoke that fails must not report a failed share.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState<ImportMode>('share');
  const [importSourceId, setImportSourceId] = useState('');

  const dashboards = useQuery({ queryKey: ['admin-dashboards'], queryFn: fetchDashboards });
  const catalogue = useGrafanaDashboards();

  const roles = definedRoles.data ?? [];
  // The role codes are open (UC61-03), so the offer is whatever the definition
  // table holds today. Landing on the first one beats an empty screen; the URL
  // carries the choice so a role's page can be linked to.
  const role = searchParams.get('role') || roles[0]?.roleCode || '';
  // A dashboard shared with OBSERVER reaches every observer code, so a role code
  // that SIMSM does not define would still collect those rows and read as if
  // they were its own grants. A code that is not on the definition list is named
  // as unknown instead.
  const knownRole = role !== '' && roles.some((row) => row.roleCode === role);
  const roleUnresolved = role !== '' && definedRoles.isSuccess && !knownRole;

  function selectRole(next: string): void {
    const params = new URLSearchParams(searchParams);
    params.set('section', 'by-role');
    if (next) params.set('role', next);
    else params.delete('role');
    setSearchParams(params, { replace: true });
    setErrorKey(null);
  }

  const rows = dashboards.data ?? [];
  const scored = rows.map((dashboard) => ({
    dashboard,
    kind: grantKindFor(dashboard.roleScope, role),
  }));
  const reached = scored.filter((row) => row.kind !== 'none');
  const candidates = scored.filter((row) => row.kind === 'none').map((row) => row.dashboard);
  const counts = {
    specific: reached.filter((row) => row.kind === 'specific').length,
    family: reached.filter((row) => row.kind === 'observer-family').length,
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['admin-dashboards'] });
    queryClient.invalidateQueries({ queryKey: ['dashboards-list'] });
  };

  // One (dashboard, role) pair per call, never a replace: granting or revoking
  // here cannot erase a grant it does not name, which is what a full-set write
  // risks the moment two administrators are on the screen at once.
  const share = useMutation({
    mutationFn: (dashboardId: string) =>
      apiClient.post(`/admin/dashboards/${dashboardId}/roles`, { roleCode: role }),
    onSuccess: () => {
      invalidate();
      setImportOpen(false);
      setImportSourceId('');
    },
    onError: (e: unknown) =>
      setErrorKey(apiErrorKey(e, 'dashboards_admin.by_role.error.share_failed')),
  });

  const revoke = useMutation({
    mutationFn: (dashboardId: string) =>
      apiClient.delete(`/admin/dashboards/${dashboardId}/roles/${encodeURIComponent(role)}`),
    onSuccess: invalidate,
    onError: (e: unknown) =>
      setErrorKey(apiErrorKey(e, 'dashboards_admin.by_role.error.revoke_failed')),
  });

  // A copy exists to be changed, so it lands inactive and the administrator
  // continues straight into its form. A half-adjusted duplicate must never be
  // what an observer opens.
  const copy = useMutation({
    mutationFn: async (source: DashboardView) => {
      const identity = duplicateIdentity(source, role, roleLabel(role), {
        slugs: rows.map((d) => d.slug),
        titles: rows.map((d) => d.title),
      });
      await apiClient.post('/admin/dashboards', {
        slug: identity.slug,
        title: identity.title,
        description: source.description,
        roleScope: [role],
        grafanaUid: source.grafanaUid,
        refreshIntervalSeconds: source.refreshIntervalSeconds,
        defaultRange: source.defaultRange,
        maxRange: source.maxRange,
        maxLookback: source.maxLookback,
        active: false,
      });
      return identity.slug;
    },
    onSuccess: (slug) => {
      invalidate();
      setImportOpen(false);
      setImportSourceId('');
      navigate(`/system-admin/dashboards-admin/${slug}/edit`);
    },
    onError: (e: unknown) =>
      setErrorKey(apiErrorKey(e, 'dashboards_admin.by_role.error.copy_failed')),
  });

  // The Grafana dashboard the row embeds, named as the administrator knows it.
  function sourceSummary(dashboard: DashboardView): string {
    if (!dashboard.grafanaUid) return t('dashboards_admin.source.missing');
    return (
      (catalogue.data ?? []).find((row) => row.uid === dashboard.grafanaUid)?.title ??
      dashboard.grafanaUid
    );
  }

  function holders(dashboard: DashboardView): string {
    // An empty scope is shared with nobody: the System Administrator only.
    return dashboard.roleScope.length === 0
      ? t('dashboards_admin.not_shared')
      : dashboard.roleScope.map(roleLabel).join(', ');
  }

  async function askRevoke(dashboard: DashboardView): Promise<void> {
    const ok = await confirm({
      title: t('dashboards_admin.by_role.revoke.title'),
      body: stillReachedAfterRevoke(dashboard.roleScope, role)
        ? t('dashboards_admin.by_role.revoke.body_still_reached', {
            title: dashboard.title,
            role: roleLabel(role),
          })
        : t('dashboards_admin.by_role.revoke.body', {
            title: dashboard.title,
            role: roleLabel(role),
          }),
      confirmLabel: t('dashboards_admin.by_role.revoke.confirm'),
      destructive: true,
    });
    if (!ok) return;
    setErrorKey(null);
    revoke.mutate(dashboard.id);
  }

  const importSource = candidates.find((d) => d.id === importSourceId);

  // The same AGE table as the dashboard-first list, read from the role's side:
  // the dashboard, the Grafana source, how this role reaches it, and the row
  // controls trailing the data as the wireframe draws them. Every cell is
  // slotted so mud-table's nowrap `.cell-text` never ellipsises a title.
  const columns: TableColumn[] = [
    { key: 'dashboard', label: t('dashboards_admin.column.dashboard') },
    { key: 'source', label: t('dashboards_admin.column.source') },
    { key: 'reach', label: t('dashboards_admin.by_role.column.reach') },
    { key: 'actions', label: '' },
  ];
  const tableRows: TableRowData[] = reached.map(({ dashboard }) => ({ id: dashboard.id }));

  return (
    <section className="simsm-page simsm-dashboards-admin__by-role">
      <div className="simsm-dashboards-admin__panel-head">
        <p className="simsm-dashboards-admin__panel-lede">{t('dashboards_admin.by_role.lede')}</p>
      </div>

      {errorKey ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t(errorKey)}
        </MudInfoBox>
      ) : null}

      <div className="simsm-dashboards-admin__role-bar">
        <MudSelect
          size="medium"
          value={role}
          label={t('dashboards_admin.by_role.role')}
          placeholder={t('dashboards_admin.by_role.role_placeholder')}
          onMudChange={(e: CustomEvent<{ value: string }>) => selectRole(e.detail.value)}
        >
          {roles.map((row) => (
            <option key={row.roleCode} value={row.roleCode}>
              {roleLabel(row.roleCode)}
            </option>
          ))}
        </MudSelect>
        <span className="simsm-dashboards-admin__role-summary">
          {knownRole
            ? t('dashboards_admin.by_role.summary', {
                total: reached.length,
                specific: counts.specific,
                family: counts.family,
              })
            : t('dashboards_admin.by_role.no_role')}
        </span>
        <span className="simsm-dashboards-admin__role-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            disabled={!knownRole}
            onClick={() => {
              setErrorKey(null);
              setImportMode('share');
              setImportSourceId('');
              setImportOpen(true);
            }}
          >
            <MudIcon slot="icon-start" name="download" size={16} />
            {t('dashboards_admin.by_role.action.import')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!knownRole}
            onClick={() =>
              navigate(`/system-admin/dashboards-admin/new?role=${encodeURIComponent(role)}`)
            }
          >
            <MudIcon slot="icon-start" name="plus-large" size={16} />
            {t('dashboards_admin.by_role.action.create')}
          </MudButton>
        </span>
      </div>

      <MudInfoBox
        emphasis="strong"
        variant="info"
        titleText={t('dashboards_admin.by_role.grafana.title')}
      >
        {t('dashboards_admin.by_role.grafana.body')}{' '}
        <MudLink
          href={grafanaEditorSrc({ kind: 'new' })}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
        >
          {t('dashboards_admin.by_role.grafana.link')}
        </MudLink>
      </MudInfoBox>

      {roleUnresolved ? (
        <MudInfoBox emphasis="strong" variant="warning">
          {t('dashboards_admin.by_role.unknown_role', { role })}
        </MudInfoBox>
      ) : (
        <QueryState
          query={dashboards}
          isEmpty={reached.length === 0}
          empty={
            /* The wireframe has no by-role editor, so this keeps the reading it
               can defend. `reached` is a filter call and is NOT the filtered-list
               defect spec 009 found: on this screen the role IS the register's
               subject, not a narrowing of it. The select above defines what the
               register is — "what this role reaches" — and drives the summary
               line and both buttons with it. There is no unfiltered register
               underneath to keep on screen, so the card is the whole answer,
               where a filter bar's empty keeps its table (Nefinalizate.tsx:60).
               No action on the card: Import and Create sit in the bar directly
               above it, and the wireframe gives an empty state at most one. */
            knownRole ? (
              <EmptyState
                icon="chart"
                title={t('dashboards_admin.by_role.empty.title', { role: roleLabel(role) })}
                body={t('dashboards_admin.by_role.empty.body')}
              />
            ) : (
              <EmptyState icon="chart" title={t('dashboards_admin.by_role.no_role')} />
            )
          }
        >
          <div className="simsm-dashboards-admin__table">
            <MudTable
              className="simsm-table"
              columns={columns}
              rows={tableRows}
              rowStyle="zebra"
              disableSort={true}
              aria-label={t('dashboards_admin.by_role.table_aria', { role: roleLabel(role) })}
              // The whole row opens the editor, the row's one visible button.
              hoverable
              onMudRowClick={onRowOpen((index) => {
                const row = reached[index];
                if (row) navigate(`/system-admin/dashboards-admin/${row.dashboard.slug}/edit`);
              })}
            >
              <span slot="header-cell-actions" className="visually-hidden">
                {t('dashboards_admin.column.actions')}
              </span>
              {reached.map(({ dashboard, kind }, rowIndex) => {
                const block = revokeBlockFor(dashboard.roleScope, role);
                return [
                  <span
                    key={`dashboard-${dashboard.id}`}
                    slot={`cell-dashboard-${rowIndex}`}
                    className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--name"
                  >
                    <span className="simsm-dashboards-admin__cell-title">
                      <strong>{dashboard.title}</strong>
                      {dashboard.active ? null : (
                        <MudTag
                          variant="status"
                          semantic="neutral"
                          label={t('dashboards_admin.status.inactive')}
                        />
                      )}
                    </span>
                    <span className="simsm-dashboards-admin__cell-meta">
                      <span className="simsm-dashboards-admin__cell-meta-key">
                        {t('dashboards_admin.meta.route')}
                      </span>{' '}
                      <code className="simsm-dashboards-admin__cell-slug">
                        /dashboards/{dashboard.slug}
                      </code>
                    </span>
                  </span>,
                  <span
                    key={`source-${dashboard.id}`}
                    slot={`cell-source-${rowIndex}`}
                    className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--source"
                  >
                    <span>{sourceSummary(dashboard)}</span>
                  </span>,
                  <span
                    key={`reach-${dashboard.id}`}
                    slot={`cell-reach-${rowIndex}`}
                    className="simsm-dashboards-admin__cell simsm-dashboards-admin__cell--stack simsm-dashboards-admin__cell--reach"
                  >
                    <MudTag
                      variant="status"
                      semantic={TAG_SEMANTIC[kind as Exclude<GrantKind, 'none'>]}
                      label={t(`dashboards_admin.by_role.reach.${kind}`)}
                    />
                    <span className="simsm-dashboards-admin__cell-meta">
                      {block
                        ? t(`dashboards_admin.by_role.locked.${block}`)
                        : t('dashboards_admin.by_role.locked.revocable', {
                            roles: holders(dashboard),
                          })}
                    </span>
                  </span>,
                  <span
                    key={`actions-${dashboard.id}`}
                    slot={`cell-actions-${rowIndex}`}
                    className="simsm-dashboards-admin__cell-actions"
                  >
                    <MudButton
                      shape="circular"
                      variant="neutral"
                      size="sm"
                      onClick={() =>
                        navigate(`/system-admin/dashboards-admin/${dashboard.slug}/edit`)
                      }
                    >
                      {t('dashboards_admin.action.edit')}
                    </MudButton>
                    <RowActionsMenu
                      label={t('dashboards_admin.action.more')}
                      actions={[
                        {
                          label: t('dashboards_admin.action.view'),
                          onSelect: () => navigate(`/system-admin/dashboards/${dashboard.slug}`),
                        },
                        {
                          label: t('dashboards_admin.by_role.action.revoke'),
                          onSelect: () => askRevoke(dashboard),
                          disabled: block !== null || revoke.isPending,
                          danger: true,
                        },
                      ]}
                    />
                  </span>,
                ];
              })}
            </MudTable>
          </div>
        </QueryState>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={importOpen}
        size="md"
        titleText={t('dashboards_admin.by_role.import.title', { role: roleLabel(role) })}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setImportOpen(false))}
      >
        <div className="simsm-dashboards-admin__import">
          <MudSegmentedControl
            size="md"
            value={importMode}
            aria-label={t('dashboards_admin.by_role.import.mode_aria')}
            segments={[
              { value: 'share', label: t('dashboards_admin.by_role.import.mode_share') },
              { value: 'copy', label: t('dashboards_admin.by_role.import.mode_copy') },
            ]}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setImportMode(e.detail.value === 'copy' ? 'copy' : 'share')
            }
          />
          <MudInfoBox emphasis="strong" variant="info">
            {t(`dashboards_admin.by_role.import.explain_${importMode}`)}
          </MudInfoBox>
          {candidates.length === 0 ? (
            <MudInfoBox emphasis="strong" variant="info">
              {t('dashboards_admin.by_role.import.empty')}
            </MudInfoBox>
          ) : (
            <MudRadioGroup
              aria-label={t('dashboards_admin.by_role.import.group')}
              className="simsm-dashboards-admin__import-list"
              value={importSourceId}
              onMudChange={(e: CustomEvent<{ value: string | undefined }>) => {
                if (e.detail.value) setImportSourceId(e.detail.value);
              }}
            >
              {candidates.map((dashboard) => (
                <MudRadio key={dashboard.id} value={dashboard.id}>
                  <span slot="label">{dashboard.title}</span>
                  <span slot="supporting-text">
                    {t('dashboards_admin.by_role.import.source_meta', {
                      roles: holders(dashboard),
                      widgets: sourceSummary(dashboard),
                    })}
                  </span>
                </MudRadio>
              ))}
            </MudRadioGroup>
          )}
        </div>
        <div slot="actions" className="simsm-dashboards-admin__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setImportOpen(false)}
          >
            {t('dashboards_admin.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!importSource || share.isPending || copy.isPending}
            onClick={() => {
              if (!importSource) return;
              setErrorKey(null);
              if (importMode === 'share') share.mutate(importSource.id);
              else copy.mutate(importSource);
            }}
          >
            {t(
              importMode === 'share'
                ? 'dashboards_admin.by_role.import.submit_share'
                : 'dashboards_admin.by_role.import.submit_copy',
              { role: roleLabel(role) },
            )}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
